import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth/store';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Calendar, MapPin, Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, Anchor, Sunset, Ship, Wind, Coffee, BedDouble, Utensils, Music, Sparkles, Instagram, ExternalLink, Globe, ChevronLeft, ChevronRight, CheckCircle, Star, Shield, Heart, Share2, Check, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';
import { SunscapesRegistrationForm } from '@/components/experiences/SunscapesRegistrationForm';
import { supabase } from '@/lib/supabase/client';
import SunscapesCheckoutModal from '@/components/experiences/SunscapesCheckoutModal';
import { useStripeCheckoutStatus } from '@/hooks/useStripeCheckoutStatus';
import { useUserPurchaseStatus } from '@/hooks/useUserPurchaseStatus';
import { PostPurchaseShareModal } from '@/components/experiences/PostPurchaseShareModal';
import { getFirstImageUrl } from '@/lib/utils/bookingHelpers';
import { LocationMap } from '@/components/experiences/LocationMap';
import { BookingButton } from '@/components/experiences/BookingButton';
import { SingleExperienceCart } from '@/components/SingleExperienceCart';
import { createBookingAndRedirectToStripe } from '@/lib/stripe';
import { useBookingsStore } from '@/lib/stores/bookings-store';
import { useExperiencesStore } from '@/lib/stores/experiences-store';

// Define interface for gallery media items
interface GalleryMedia {
  url: string;
  type: 'image' | 'video';
  previewUrl?: string; // For video thumbnails
}

interface Venue {
  name: string;
  mapsQuery: string;
  mapsUrl: string;
}

// Static fallback for Sunscapes experience
const SUNSCAPES_EXPERIENCE = {
  id: '9611812e-270f-4379-8cc9-801ccce37139',
  media: [
    { url: 'https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01195f628e89e6b468c55065f6af4bc143ad283efa.jpg', type: 'image', order_index: 0 }
  ],
  // ...other required fields as needed
};

export function SunscapesExperience() {
  // The Sunscapes experience ID
  const SUNSCAPES_EXPERIENCE_ID = '9611812e-270f-4379-8cc9-801ccce37139';
  
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  const { hasPurchased, isLoading: purchaseLoading } = useUserPurchaseStatus(SUNSCAPES_EXPERIENCE_ID);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [currentAccommodationGallery, setCurrentAccommodationGallery] = useState('main');
  const [currentAccommodationIndex, setCurrentAccommodationIndex] = useState(0);
  const [currentAlternativeIndex, setCurrentAlternativeIndex] = useState(0);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [preselectedProduct, setPreselectedProduct] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('about');
  const locationMapRef = useRef<HTMLDivElement>(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isGalleryImageLoading, setIsGalleryImageLoading] = useState(true);
  
  const { experiences } = useExperiencesStore();
  const experience = experiences.find(exp => exp.id === SUNSCAPES_EXPERIENCE.id) || SUNSCAPES_EXPERIENCE;
  const firstImageUrl = getFirstImageUrl(experience);

  // Gallery media from CDN
  const galleryMedia: GalleryMedia[] = [
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01195f628e89e6b468c55065f6af4bc143ad283efa.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01292f314ffdfb62e9341372f11a24d5491c82d6a2.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/013a9f0c0a82822d2337d28f5976e5bd52015e4519.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/015023011147d85299f191e7110ec22ad1521cc660.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/016c7680e890bed2cfbc602750a4902a7e7848839b.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/017107e8d6e74e944e3eef1f4de3dc6b0c8c311cb4.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01875940163398c4f0d490f874eee9c75eaef324b5.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/0187f0f22c198baa5fde67a4737ab8276ed9a308aa.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/018d4048bdfc184771681f627c92dba9556fbddf6b_00001.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01a09b429b80baa7f84a0fab8704ce75aa017f8b6c.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01a17d58fdee99ebe514ed518c77cabdeaf3c3e7b1.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01a0d393a8de55f827ccb6a031bfc2ac63abf95b8f.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01ce54c14d237fc7d0216189c81a57e206828da2d2.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01a17d58fdee99ebe514ed518c77cabdeaf3c3e7b1.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01ab768b6bb35afea5ce8d09c4db33fe498699ce3a.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/016c7680e890bed2cfbc602750a4902a7e7848839b.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01c9e2e5ec3d394edf7414aa6be9960426ddaea174.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01292f314ffdfb62e9341372f11a24d5491c82d6a2.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01cbd0f673becb32512a9b72d60e5b90e17ff693d1.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01195f628e89e6b468c55065f6af4bc143ad283efa.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01ce54c14d237fc7d0216189c81a57e206828da2d2.jpg",
      type: "image",
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01d763eceb4507549d0c5bdaa07b020fd87c32cf41.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/017107e8d6e74e944e3eef1f4de3dc6b0c8c311cb4.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01e858a6426a0c295abf4dae128db29d78c6d2f07f.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/015023011147d85299f191e7110ec22ad1521cc660.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01ed0730e6a7d72bfe976dbdf39398c7148524c1e4.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/01875940163398c4f0d490f874eee9c75eaef324b5.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/Untitled%20video.mp4",
      type: "video",
      previewUrl: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/013a9f0c0a82822d2337d28f5976e5bd52015e4519.jpg"
    },
    {
      url: "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/018d4048bdfc184771681f627c92dba9556fbddf6b_00001.jpg",
      type: "image"
    }
  ];

  // Accommodation galleries
  const accommodationGalleries = {
    main: [
      "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/SKUFINSKA-25.png",
      "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/8T8A8822.png",
      "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/hotel_detail.png",
      "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/018cf6194282b3ab962f994cb8c29d4a076e1cdf20.jpg"
    ],
    alternative: [
      "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/boat-sunset.jpeg",
      "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/SKUFINSKA-25.png"
    ]
  };
  
  // Handle post-purchase sharing
  useStripeCheckoutStatus({
    customSuccessMessage: "Your Sunscapes experience is confirmed! Get ready for an incredible journey.",
    onSuccess: () => {
      // Show share modal after successful purchase
      setTimeout(() => setShowShareModal(true), 1000);
    }
  });

  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Check if user is already registered when they log in
  useEffect(() => {
    const checkRegistration = async () => {
      if (!user) return;
      
      setIsCheckingRegistration(true);
      try {
        const { data, error } = await supabase
          .from('sunscapes_registrations')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) throw error;
        setIsRegistered(!!data);
      } catch (error) {
        console.error('Failed to check registration status:', error);
      } finally {
        setIsCheckingRegistration(false);
      }
    };

    checkRegistration();
  }, [user, supabase]);
  
  // Add at the top of the component:
  const sunscapesVenues: Venue[] = [
    {
      name: "Before Sunset Beach Club",
      mapsQuery: "Before Sunset Beach Club Cesme",
      mapsUrl: "https://maps.google.com/?q=Before+Sunset+Beach+Club+Cesme"
    },
    {
      name: "Radisson Blu Resort & Spa Cesme",
      mapsQuery: "Radisson Blu Resort & Spa Cesme",
      mapsUrl: "https://maps.google.com/?q=Radisson+Blu+Resort+%26+Spa+Cesme"
    }
  ];

  useEffect(() => {
    if (activeTab !== 'about' || !locationMapRef.current || !window.google?.maps) return;

    try {
      // Clear previous map instance
      locationMapRef.current.innerHTML = '';

      // Initialize the map
      const map = new window.google.maps.Map(locationMapRef.current, {
        center: { lat: 38.322, lng: 26.305 },
        zoom: 11,
        mapTypeId: "roadmap",
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_BOTTOM
        },
        styles: [
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
          { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] }
        ]
      });

      const service = new window.google.maps.places.PlacesService(map);

      sunscapesVenues.forEach(venue => {
        service.textSearch({ query: venue.mapsQuery }, (results: google.maps.places.PlaceResult[] | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
            const place = results[0];
            const marker = new window.google.maps.Marker({
              map,
              position: place.geometry?.location,
              title: venue.name,
              animation: window.google.maps.Animation.DROP
            });
            const contentString = `
              <div style="padding: 12px; max-width: 260px; background: #f8f8f8; border-radius: 10px; box-shadow: 0 2px 12px rgba(0,0,0,0.15); border: 1px solid #bbb; color: #222; font-family: Arial, sans-serif;">
                <h4 style=\"margin: 0 0 6px 0; font-size: 16px; font-weight: 700; color: #18181b;\">${venue.name}</h4>
                <p style=\"margin: 0 0 8px 0; font-size: 13px; color: #333; font-weight: 500;\">${place.formatted_address || ''}</p>
                <a href=\"${venue.mapsUrl}\" target=\"_blank\" style=\"color: #2563eb; text-decoration: underline; font-size: 13px; font-weight: 600;\">Open in Google Maps →</a>
              </div>
            `;
            const infoWindow = new window.google.maps.InfoWindow({
              content: contentString,
              maxWidth: 260
            });
            marker.addListener("click", () => infoWindow.open(map, marker));
          }
        });
      });
    } catch (error) {
      console.error('Failed to initialize Google Maps:', error);
      // Show a fallback message or image if map fails to load
      if (locationMapRef.current) {
        locationMapRef.current.innerHTML = '<div class="p-4 text-center text-sand-300">Failed to load map. Please try refreshing the page.</div>';
      }
    }
  }, [activeTab, sunscapesVenues]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video immediately after the page loads
      setFreezeFrameShowing(false);
      video.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.warn('Autoplay failed:', err);
          setIsPlaying(false);
        });
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleReserveClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    setShowRegistration(true);
  };

  const handleRegistrationSuccess = () => {
    setShowRegistration(false);
    setIsRegistered(true);
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Gallery navigation handlers
  const navigateGallery = (direction: 'next' | 'prev') => {
    setCurrentGalleryIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % galleryMedia.length;
      } else {
        return prev === 0 ? galleryMedia.length - 1 : prev - 1;
      }
    });
  };

  const navigateAccommodationGallery = (direction: 'next' | 'prev') => {
    const currentGallery = accommodationGalleries[currentAccommodationGallery as keyof typeof accommodationGalleries];
    setCurrentAccommodationIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % currentGallery.length;
      } else {
        return prev === 0 ? currentGallery.length - 1 : prev - 1;
      }
    });
  };

  // Handler for purchase buttons
  const handlePurchaseClick = (productKey: string | null = null) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setPreselectedProduct(productKey);
    setShowCheckoutModal(true);
  };

  // Centralize purchase/share logic
  const handleUnifiedPurchaseClick = () => {
    setShowCheckoutModal(true);
  };
  const handleBringFriendsClick = () => {
    setShowShareModal(true);
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Sunscapes - Cesme | rendezvous"
        description="Experience four days of sun, music, and wellness at the breathtaking Before Sunset Beach Club in Cesme, Turkey."
        image="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/PHOTO-2024-06-27-11-04-34.jpg"
        url="https://letsrendezvous.co/experiences/sunscapes"
      />

      {/* Hero Section with Video */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center bg-earth-900 overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/PHOTO-2024-06-27-11-04-34.jpg"
              alt="Sunscapes Cesme"
              className="w-full h-full object-cover object-center"
            />
          </div>
          
          {/* Video Element */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/IMG_4462.MOV"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted={isMuted}
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/PHOTO-2024-06-27-11-04-34.jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content - Showing Immediately */}
        <div className="absolute inset-0 top-20 flex flex-col items-center justify-end px-4 text-center pb-36">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display mb-6">Sunscapes</h1>
            <p className="text-xl md:text-2xl text-sand-200 mb-8">The second edition in Cesme, Turkey</p>
            <p className="text-xl md:text-1xl text-sand-200 mb-8">July 10-14th</p>
            <div className="relative z-50">
              {hasPurchased ? (
                <Button
                  className="w-full mt-4"
                  onClick={handleBringFriendsClick}
                >
                  <Users className="h-5 w-5 mr-2" />
                  Bring Your Friends
                </Button>
              ) : (
                <Button
                  className="w-full mt-4"
                  onClick={handleUnifiedPurchaseClick}
                >
                  Purchase Experience
                </Button>
              )}
            </div>
          </motion.div>

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-sand-300 hover:text-sand-100 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Content Section with Tabs */}
      <div id="content-section" className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="about" className="space-y-8">
                <div className="overflow-x-auto -mx-4 px-4">
                  <TabsList className="w-full min-w-max">
                    <TabsTrigger value="about" onClick={() => setActiveTab('about')}>About</TabsTrigger>
                    <TabsTrigger value="itinerary" onClick={() => setActiveTab('itinerary')}>Itinerary</TabsTrigger>
                    <TabsTrigger value="accommodation" onClick={() => setActiveTab('accommodation')}>Accommodation</TabsTrigger>
                    <TabsTrigger value="location" onClick={() => setActiveTab('location')}>Location</TabsTrigger>
                    <TabsTrigger value="faq" onClick={() => setActiveTab('faq')}>FAQ</TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="about" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">About Sunscapes</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      Experience the return of Sunscapes - a four-day celebration of music, wellness, and connection in the stunning coastal paradise of Cesme, Turkey. Building on the success of the first edition, this year promises an even more extraordinary experience at the beautiful Before Sunset Beach Club.
                    </p>
                    <p>
                      Sunscapes is carefully curated to balance daytime wellness activities with vibrant nighttime celebrations. From beachfront yoga sessions to boat excursions and intimate dinner experiences, every element has been thoughtfully designed to create four unforgettable days of Mediterranean magic.
                    </p>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">What's Included</h3>
                      <div className="space-y-2">
                        <p>Your experience includes access to all event programming across four days, with options for half or full participation:</p>
                        <ul className="list-disc list-inside space-y-2 pl-4">
                          <li>Welcome reception and evening party at Before Sunset</li>
                          <li>Full wellness program with expert facilitators</li>
                          <li>Sunday showcase party from day to night</li>
                          <li>Optional add-ons for the full Sunscapes experience</li>
                          <li>Community of like-minded travelers and music lovers</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Image and Video Gallery with CDN Content */}
                  <div className="mt-10 relative">
                    <h3 className="text-xl font-display mb-6">Gallery</h3>
                    <div className="relative aspect-video rounded-lg overflow-hidden">
                      {galleryMedia[currentGalleryIndex].type === 'video' ? (
                        <video
                          src={galleryMedia[currentGalleryIndex].url}
                          poster={galleryMedia[currentGalleryIndex].previewUrl || galleryMedia[currentGalleryIndex].url}
                          className="w-full h-full object-cover"
                          controls
                          onLoadStart={() => setIsGalleryImageLoading(true)}
                          onLoadedData={() => setIsGalleryImageLoading(false)}
                        />
                      ) : (
                        <>
                          {isGalleryImageLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-earth-800">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sand-300"></div>
                            </div>
                          )}
                          <img 
                            src={galleryMedia[currentGalleryIndex].url} 
                            alt={`Sunscapes Gallery Image ${currentGalleryIndex + 1}`} 
                            className={`w-full h-full object-cover transition-opacity duration-300 ${isGalleryImageLoading ? 'opacity-0' : 'opacity-100'}`}
                            onLoadStart={() => setIsGalleryImageLoading(true)}
                            onLoad={() => setIsGalleryImageLoading(false)}
                          />
                        </>
                      )}
                      
                      {/* Navigation Arrows */}
                      <button 
                        onClick={() => {
                          setIsGalleryImageLoading(true);
                          navigateGallery('prev');
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800/80 transition-colors"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      
                      <button 
                        onClick={() => {
                          setIsGalleryImageLoading(true);
                          navigateGallery('next');
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800/80 transition-colors"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                      
                      {/* Image Count Indicator */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 text-sm">
                        {currentGalleryIndex + 1} / {galleryMedia.length}
                      </div>
                    </div>
                    
                    {/* Thumbnail Navigation */}
                    <div className="flex gap-2 mt-4 justify-center overflow-x-auto pb-2">
                      {galleryMedia.map((media, index) => (
                        <button 
                          key={index} 
                          onClick={() => setCurrentGalleryIndex(index)}
                          className={`w-16 h-10 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                            currentGalleryIndex === index 
                              ? 'border-sand-300 opacity-100' 
                              : 'border-transparent opacity-60 hover:opacity-80'
                          }`}
                        >
                          {media.type === 'video' ? (
                            <div className="relative w-full h-full">
                              <img 
                                src={media.previewUrl || galleryMedia[0].url} 
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-earth-900/50">
                                <Play className="h-4 w-4 text-sand-300" />
                              </div>
                            </div>
                          ) : (
                            <img 
                              src={media.url} 
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">Location</h3>
                      <div>
                        <div className="w-full h-[350px] rounded-lg overflow-hidden mb-8">
                          <div ref={locationMapRef} className="w-full h-full" />
                        </div>
                      </div>
                    </div>
              </TabsContent>  

                
                <TabsContent value="itinerary" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Four-Day Journey</h2>
                  <div className="space-y-8">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Thursday - Welcome</h3>
                      <p className="text-sand-300 mb-4">@Before Sunset Beach Club</p>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Afternoon arrival and check-in</li>
                        <li>Welcome reception with sunset cocktails</li>
                        <li>Opening night party with resident DJs</li>
                        <li>Late night beach sessions</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Friday - Wellness & Sounds</h3>
                      <p className="text-sand-300 mb-4">@Before Sunset Beach Club & Elefante</p>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Morning wellness sessions (yoga, meditation, breathwork)</li>
                        <li>Afternoon beach time and relaxation</li>
                        <li>Evening transfer to Elefante</li>
                        <li>Live music dinner experience at Elefante (full experience only)</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Saturday - Ocean & Alacati</h3>
                      <p className="text-sand-300 mb-4">@Mediterranean Sea & Alacati</p>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Full-day boat excursion with swimming and lunch (full experience only)</li>
                        <li>Return to hotel for refresh</li>
                        <li>Evening in Alacati's charming streets</li>
                        <li>Dinner at selected Alacati restaurant</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Sunday - Sunscapes Showcase</h3>
                      <p className="text-sand-300 mb-4">@Before Sunset Beach Club</p>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Morning beach yoga to start the day</li>
                        <li>Midday wellness activities</li>
                        <li>Afternoon beach party with featured DJs</li>
                        <li>Sunset celebration ritual</li>
                        <li>Main showcase party into the night</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Optional Add-Ons</h3>
                      <ul className="list-disc list-inside space-y-2 text-sand-300">
                        <li>Windsports lessons and equipment rental</li>
                        <li>Day trip to historic Urla with wine tasting</li>
                        <li>Guided tour of ancient Ephesus</li>
                        <li>Private massage and spa treatments</li>
                      </ul>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="accommodation" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Where You'll Stay</h2>
                  <p className="text-sand-300 mb-8">
                    For Sunscapes, we've carefully selected accommodations that enhance your experience while providing comfort and convenience.
                  </p>
                  
                  <div className="space-y-12">
                    {/* Main Accommodation with Gallery */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <div className="relative aspect-video">
                        <img 
                          src={accommodationGalleries.main[currentAccommodationIndex]} 
                          alt="Before Sunset Beach Club" 
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Gallery Navigation */}
                        {accommodationGalleries.main.length > 1 && (
                          <>
                            <button 
                              onClick={() => {
                                setCurrentAccommodationGallery('main');
                                navigateAccommodationGallery('prev');
                              }}
                              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800/80 transition-colors"
                            >
                              <ChevronLeft className="h-6 w-6" />
                            </button>
                            
                            <button 
                              onClick={() => {
                                setCurrentAccommodationGallery('main');
                                navigateAccommodationGallery('next');
                              }}
                              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800/80 transition-colors"
                            >
                              <ChevronRight className="h-6 w-6" />
                            </button>
                            
                            {/* Image Counter */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 text-sm">
                              {currentAccommodationIndex + 1} / {accommodationGalleries.main.length}
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="p-6">
                        <div className="mb-2">
                          <h3 className="text-2xl font-display">Before Sunset Beach Club & Hotel</h3>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sand-100">Dual Occupancy:</span>
                            <span className="text-sand-100">€525/night</span>
                            <span className="text-sand-400 text-xs">(€2,100 total)</span>
                          </div>
                        </div>
                        <span className="text-sand-300 text-xs mb-2 block">Signature Turkish breakfast available until 2pm</span>
                        <p className="text-sand-300 mb-2">DM us if you are traveling alone and want to share a room.</p>
                        <p className="text-sand-300 mb-4">
                          Our main venue and accommodation partner, Before Sunset offers stylish rooms just steps from the water. We've privatized a section of 25 rooms exclusively for Sunscapes guests, creating our own community within the property.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-sand-300">
                            <BedDouble className="h-4 w-4" />
                            <span>Beachfront Rooms</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <Coffee className="h-4 w-4" />
                            <span>Breakfast Included</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <Sunset className="h-4 w-4" />
                            <span>Direct Beach Access</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <Utensils className="h-4 w-4" />
                            <span>Restaurant & Bar</span>
                          </div>
                        </div>
                        <p className="text-sand-300 text-sm">
                          Website: <a href="https://www.beforesunsetcesme.com/hotel" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:underline">beforesunsetcesme.com/hotel</a>
                        </p>
                      </div>
                    </div>

                    {/* Alternative Accommodation with Gallery */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <div className="relative aspect-video">
                        {/* Image Gallery for Radisson Blu Resort & Spa Cesme */}
                        <img 
                          src={[
                            "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Radissun%20Pics/16256-114103-f64518962_4k.webp",
                            "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Radissun%20Pics/16256-114103-f72620903_4k.avif",
                            "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Radissun%20Pics/16256-114103-f72620915_4k.avif",
                            "https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Radissun%20Pics/16256-114103-f73390962_4k.avif"
                          ][currentAlternativeIndex % 4]}
                          alt="Radisson Blu Resort & Spa Cesme"
                          className="w-full h-full object-cover"
                        />
                        {/* Gallery Navigation */}
                        <>
                          <button 
                            onClick={() => setCurrentAlternativeIndex(prev => (prev === 0 ? 3 : prev - 1))}
                            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800/80 transition-colors"
                          >
                            <ChevronLeft className="h-6 w-6" />
                          </button>
                          <button 
                            onClick={() => setCurrentAlternativeIndex(prev => (prev + 1) % 4)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800/80 transition-colors"
                          >
                            <ChevronRight className="h-6 w-6" />
                          </button>
                          {/* Image Counter */}
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 text-sm">
                            {currentAlternativeIndex + 1} / 4
                          </div>
                        </>
                      </div>
                      <div className="p-6">
                        <div className="mb-2">
                          <h3 className="text-2xl font-display">Radisson Blu Resort & Spa Cesme</h3>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sand-100">Single:</span>
                            <span className="text-sand-100">€225/night</span>
                            <span className="text-sand-400 text-xs">(€900 total)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sand-100">Double:</span>
                            <span className="text-sand-100">€240/night</span>
                            <span className="text-sand-400 text-xs">(€950 total)</span>
                          </div>
                        </div>
                        <span className="text-sand-300 text-xs mb-2 block">Breakfast included</span>
                        <p className="text-sand-300 mb-4">
                          The Radisson Blu Resort & Spa Cesme offers modern comfort and direct access to a private beach, with a large outdoor pool, spa, and a variety of dining options. Enjoy beautiful sea views, spacious rooms, and a hearty breakfast each morning. The hotel is a short drive from the event venue, with shuttle service provided for all Sunscapes guests.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2 text-sand-300">
                            <MapPin className="h-4 w-4" />
                            <span>Cesme Coast</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <BedDouble className="h-4 w-4" />
                            <span>Single & Double Rooms</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <Coffee className="h-4 w-4" />
                            <span>Breakfast Included</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <Sunset className="h-4 w-4" />
                            <span>Private Beach & Pool</span>
                          </div>
                        </div>
                        <p className="text-sand-300 mt-4 text-sm">
                          Website: <a href="https://www.radissonhotels.com/en-us/hotels/radisson-blu-resort-cesme-spa" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:underline">radissonhotels.com/en-us/hotels/radisson-blu-resort-cesme-spa</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="location" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Discover Cesme</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    <div>
                      <p className="text-sand-300 mb-4">
                        Located on the stunning Aegean coast of Turkey, Cesme is known for its crystal-clear waters, consistent winds (perfect for water sports), and charming atmosphere. Before Sunset Beach Club provides the ideal setting for our Sunscapes experience with direct beach access, beautiful sunset views, and excellent facilities.
                      </p>
                      <p className="text-sand-300 mb-4">
                        In addition to its natural beauty and vibrant culture, Cesme boasts a thriving beach club scene and thermal springs that offer the perfect mix of relaxation and indulgence. It also  provides a unique blend of luxury and authenticity that elevates your stay into something truly unforgettable.
                      </p>
                      <p className="text-sand-300">
                        During your stay, you'll also have the opportunity to explore the charming Alacati village with its stone houses and narrow streets, taste local cuisine, and experience the rich culture of this Mediterranean paradise.
                      </p>
                    </div>
                    <div>
                      {/* Here we replace the Google Map with stacked images as requested */}
                      <div className="flex flex-col gap-4">
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/Cesme%20Pics/1646677670287.jpg"
                          alt="Çeşme Landscape" 
                          className="w-full h-[200px] object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/Cesme%20Pics/4af46bcf-city-59901-168ba859b43.jpg"
                          alt="Çeşme Marina" 
                          className="w-full h-[200px] object-cover rounded-lg"
                        />
                        <img 
                          src="https://rendezvouswebsite.b-cdn.net/4.%20Sunscapes/Media%20Gallery/Cesme%20Pics/public.jpg"
                          alt="Çeşme Beach" 
                          className="w-full h-[200px] object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 bg-earth-800/50 p-6 rounded-lg">
                    <h3 className="text-xl font-display mb-4">Travel Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-sand-100 mb-2">Getting There</h4>
                        <ul className="space-y-3 text-sand-300">
                          <li className="flex items-start gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Fly to Izmir Adnan Menderes Airport (ADB)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Cesme is approximately 1 hour drive from Izmir</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Airport transfers can be arranged (additional cost)</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-sand-100 mb-2">Local Transportation</h4>
                        <ul className="space-y-3 text-sand-300">
                          <li className="flex items-start gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Regular shuttle service between accommodations</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Taxis readily available throughout Cesme</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-sand-100">•</span> 
                            <span>Transportation to excursions included with add-ons</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="faq" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-6">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What's the difference between half and full experience?</h3>
                      <p className="text-sand-300">
                        The half-experience package (€300) includes the Thursday evening reception, Friday daytime wellness program, and Sunday's full-day showcase. The full experience (€550) includes everything in the half package plus the Elefante dinner on Friday evening, Saturday's boat day, and the Alacati dinner on Saturday night. Add-ons for Elefante and the boat day can be purchased separately if you choose the half package.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What should I pack?</h3>
                      <p className="text-sand-300">
                        Pack light summer clothing, swimwear, sunscreen, and a hat for daytime activities. For evenings, we recommend smart casual attire for dinners. If you're interested in windsports, athletic wear would be useful. Don't forget comfortable shoes for exploring Alacati and any excursions you might join.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">How do I book add-on experiences?</h3>
                      <p className="text-sand-300">
                        After booking your main Sunscapes package, you'll receive a confirmation email with links to book add-on experiences. You can also add these experiences later as the event approaches, though some popular options like the boat day may have limited capacity.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What's the best way to get from Izmir Airport to Cesme?</h3>
                      <p className="text-sand-300">
                        We recommend arranging airport transfers through our partner transport company. Shared transfers are approximately €25 per person each way, while private transfers start at €70 per car. Rental cars are also a good option if you want flexibility during your stay. Contact us after booking to arrange your transfer.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What is the cancellation policy?</h3>
                      <p className="text-sand-300">
                        Cancellations made more than 60 days before the event receive a full refund minus a €50 processing fee. Cancellations between 30-60 days receive a 50% refund. Cancellations less than 30 days before the event are non-refundable, but tickets can be transferred to another person.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">Are accommodation prices separate from the event tickets?</h3>
                      <p className="text-sand-300">
                        Yes, the accommodation costs are separate from the event tickets. Rooms at the Before Sunset Beach Club are avaialable for €2,100 per room for the duration of the event, while our alternative hotel option costs €800. Both options include breakfast and provide different levels of comfort and convenience.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sand-100 font-semibold">Pricing</span>
                    <div>
                      <span className="text-sand-100 font-semibold">€300 - €550</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sand-300">
                      <Clock className="h-5 w-5 text-sand-400" />
                      <span>4 days of experiences</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-5 w-5 text-sand-400" />
                      <span>Summer 2025</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Users className="h-5 w-5 text-sand-400" />
                      <span>Limited capacity event</span>
                    </div>
                    {hasPurchased ? (
                      <Button
                        className="w-full mt-4"
                        onClick={handleBringFriendsClick}
                      >
                        <Users className="h-5 w-5 mr-2" />
                        Bring Your Friends
                      </Button>
                    ) : (
                      <Button
                        className="w-full mt-4"
                        onClick={handleUnifiedPurchaseClick}
                      >
                        Purchase Experience
                      </Button>
                    )}
                  </div>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Accommodation</h3>
                  <ul className="space-y-4">
                    <li className="border-b border-earth-700 pb-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-sand-100">Before Sunset Beach Club</span>
                        <span className="text-sand-100 font-semibold">€525/night</span>
                      </div>
                      <p className="text-sand-300 text-sm">
                        Exclusive beachfront rooms with direct access to all events (€2,200 total)
                      </p>
                    </li>
                    <li>
                      <div className="flex justify-between mb-2">
                        <span className="text-sand-100">Alternative Hotel</span>
                        <span className="text-sand-100 font-semibold">€225+/night</span>
                      </div>
                      <p className="text-sand-300 text-sm">
                        Nearby accommodations with shuttle service
                      </p>
                    </li>
                  </ul>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Host</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://rendezvouswebsite.b-cdn.net/IMG_2255.JPG" 
                      alt="Sunscapes"
                      className="w-16 h-16 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=SS&backgroundColor=1d1918&textColor=e8e4dc";
                      }}
                    />
                    <div>
                      <p className="text-sand-100 font-medium">Sunscapes</p>
                      <p className="text-sand-400 text-sm">Experience Creators</p>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm mb-4">
                    Sunscapes creates immersive experiences that blend music, wellness, and natural beauty. Their events are designed to foster connection with nature, oneself, and a community of like-minded individuals in stunning locations around the world.
                  </p>
                  <a 
                    href="https://www.instagram.com/__sunscapes/?locale=gb&hl=am-et" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    <span>@__sunscapes</span>
                  </a>
                </div>
                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Package Options</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sand-100 block">Half Experience: €300</span>
                        <span className="text-sand-300 text-sm">Thursday evening, Friday daytime, Sunday full day</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sand-100 block">Full Experience: €550</span>
                        <span className="text-sand-300 text-sm">All activities across the 4 days</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Ship className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sand-100 block">Boat Day Add-on</span>
                        <span className="text-sand-300 text-sm">Available for purchase with half experience</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Music className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sand-100 block">Elefante Dinner Add-on</span>
                        <span className="text-sand-300 text-sm">Available for purchase with half experience</span>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Activities</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Wind className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Windsports in world-class conditions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Ship className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Boat day with swimming and lunch</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Music className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Live music and DJ performances</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sunset className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Wellness programs and sunset rituals</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Excursions to Urla and Ephesus</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
                
      {/* Call to Action */}
      <div className="mt-24 bg-earth-800/50 p-8 rounded-lg text-center">
        <h2 className="text-3xl font-display mb-6">Join the Sunscapes Community</h2>
        <p className="text-sand-300 mb-8 max-w-2xl mx-auto">
          Experience four unforgettable days of music, wellness, and connection at the stunning Before Sunset Beach Club. Limited spots available for this exclusive gathering.
        </p>
        {hasPurchased ? (
          <Button
            size="lg"
            onClick={handleBringFriendsClick}
            className="text-lg px-8 py-4"
          >
            <Users className="h-5 w-5 mr-2" />
            Bring Your Friends
          </Button>
        ) : (
          <Button
            size="lg"
            onClick={handleUnifiedPurchaseClick}
            className="text-lg px-8 py-4"
          >
            Purchase Experience
          </Button>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />

      {/* Bottom-of-the-page modal for checkout/cart */}
      {showCheckoutModal && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center items-end sm:items-center sm:p-8">
          <SunscapesCheckoutModal
            isOpen={showCheckoutModal}
            onClose={() => setShowCheckoutModal(false)}
            preselectedProduct={preselectedProduct}
          />
        </div>
      )}

      {/* Post-Purchase Share Modal */}
      <PostPurchaseShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        experienceTitle="Sunscapes - 4 Days of Music, Wellness & Connection"
        experienceUrl={window.location.href}
        experienceImage={firstImageUrl}
        onBuyMoreTickets={() => {
          setShowShareModal(false);
          setShowCheckoutModal(true);
        }}
      />
    </div>
  );
}

export default SunscapesExperience;